import { ReactElement, useCallback, useState } from 'react';
import { ButtonGroup, Dropdown, NavLink, Spinner } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import { getSpreadSheetsProvider } from '../../../../services/get-data-provider.service';
import { postEncodeFilterSelection } from '../../../../services/get-encode-filter-selection.service';
import { IMAGES_REPO } from '../../../../static-resources';
import {
  ENCODED_FILTER_SEL_PAR,
  FILTER_NAMES,
} from '../../../filters/hooks/filter-names.constants';
import { ReportsComponentProps } from '../../interfaces/reports-component-props';
import './export-button.component.scss';
import { AppCopyToClipboardModal } from '../copy-to-clipboard/app-copy-to-clipboard.modal';
export const getFileName = (name?: string) => {
  if (name) {
    // Allows alphanuneric characters along with hyphen (-) and spaces.
    return name
      .trim()
      .replace(/[^a-zA-Z0-9- ]/g, '_')
      .substring(0, 30);
  } else {
    return 'Export';
  }
};
export interface ExportButtonDownloader {
  download(): void;
}
interface ExportButtonComponentProps extends ReportsComponentProps {
  filename: string;
  IconComponent?: ReactElement;
  asNavLink?: boolean;

  PDFDownloader?: ExportButtonDownloader;
}
export const ExportButtonComponent = ({
  reportName,
  reportId = '',
  filterSelection,
  filename,
  IconComponent = (
    <img
      src={IMAGES_REPO.DownloadIcon}
      alt=""
      className="demand-model-download-icon"
    />
  ),
  asNavLink = false,
  PDFDownloader,
}: ExportButtonComponentProps) => {
  const [isFetching, setIsFetching] = useState(false);
  const [isError, setIsError] = useState(false);
  const onDownloadClick = useCallback(() => {
    const link = document.createElement('a');
    link.target = '_blank';
    setIsError(false);
    setIsFetching(true);
    getSpreadSheetsProvider(
      reportName,
      reportId,
      1,
      10000,
      undefined,
      filterSelection,
    )
      .then((res: any) => {
        const marketTitle =
          filterSelection.find((i) => i.id === FILTER_NAMES.MARKET)?.values[0]
            .label ?? '';
        const metaCatTitle =
          filterSelection.find((i) => i.id === FILTER_NAMES.METACATEGORY)
            ?.values[0].label ?? '';
        link.download = `${filename}-${metaCatTitle ?? ''}-${marketTitle ?? ''}.xlsx`;
        link.href = URL.createObjectURL(
          new Blob([res.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          }),
        );
        link.click();
        setIsFetching(false);
      })
      .catch(() => {
        setIsError(true);
        setIsFetching(false);
      });
  }, [filterSelection, reportName, reportId, filename]);

  const [showPopup, setShowPopup] = useState(false);
  const [link, setLink] = useState('');
  const location = useLocation();
  //const encoded = useEncodeFilterSelection(filterSelection)
  const onCopyLink = useCallback(() => {
    const origin = window.location.origin;
    const slashEnd =
      location.pathname.lastIndexOf('/') === location.pathname.length - 1;
    const downloadRoute = slashEnd ? 'download' : '/download';
    const baseUrl = location.pathname;

    postEncodeFilterSelection(filterSelection).then((encoded) => {
      const search = `?${ENCODED_FILTER_SEL_PAR}=${encoded?.encoded}`; // location.search
      setLink(
        `${origin}${baseUrl}${downloadRoute}/${reportName}/${filename}/${search}`,
      );
      setShowPopup(true);
    });
  }, [reportName, filename, location, filterSelection]);

  const onClosePopUp = useCallback((show: boolean) => {
    setShowPopup(show);
  }, []);

  return (
    <Dropdown
      id="download-button"
      className="download-button"
      title={
        isError
          ? 'Error when retrieving the data. Please, try again.'
          : 'Download'
      }
      as={ButtonGroup}
    >
      <Dropdown.Toggle
        variant="sm"
        as={asNavLink ? NavLink : undefined}
        className="export-button-component btn-default"
        id="download-button-toggle"
        disabled={isFetching}
      >
        {isFetching ? <Spinner animation="border" size="sm" /> : IconComponent}
        {isError ? (
          <img src={IMAGES_REPO.ReportProblemIcon} alt="Error" />
        ) : null}
      </Dropdown.Toggle>
      <Dropdown.Menu align={'end'}>
        <Dropdown.Item onClick={() => onDownloadClick()}>
          <img
            src={IMAGES_REPO.ExcelFileIcon}
            alt=""
            className="dropdown-item-img"
          />
          Download as Excel
        </Dropdown.Item>
        {PDFDownloader !== undefined && (
          <Dropdown.Item onClick={() => PDFDownloader.download()}>
            <img
              src={IMAGES_REPO.PDFIcon}
              alt="Download as PDF"
              width={24}
              className="dropdown-item-img"
            />
            Download as PDF
          </Dropdown.Item>
        )}
        <Dropdown.Item onClick={() => onCopyLink()}>
          <img
            src={IMAGES_REPO.CopyIcon}
            alt=""
            className="dropdown-item-img"
          />
          Copy file link
        </Dropdown.Item>
      </Dropdown.Menu>
      <AppCopyToClipboardModal
        link={link}
        onClose={onClosePopUp}
        show={showPopup}
        title="Copy file link"
      />
    </Dropdown>
  );
};
